/**
 * ============================================================
 *  JavaCompleteAlgoPrep.jsx
 *  ─────────────────────────────────────────────────────────
 *  A single-file React + MUI study guide covering:
 *    • 8 Java Collections  (ArrayList, HashSet, HashMap,
 *      LinkedList, TreeMap, TreeSet, LinkedHashSet, LinkedHashMap)
 *    • 6 Algorithm Patterns (Binary Search, Sorting,
 *      Two Pointers, Sliding Window, HashMap Patterns,
 *      Recursion & Backtracking)
 *
 *  Clean Code Principles applied:
 *    1. Meaningful Names   – every variable/function name tells
 *                           you WHAT it is, not HOW it works.
 *    2. Single Responsibility – each component does ONE thing.
 *    3. DRY (Don't Repeat Yourself) – shared data lives in
 *                           DATA LAYER constants at the top;
 *                           shared UI lives in small components.
 *    4. Small Functions    – no function exceeds ~40 lines.
 *    5. Comments for WHY  – not "what" (code shows what,
 *                           comments explain why).
 *
 *  Author note for junior devs:
 *    Read the DATA section first, then COMPONENTS, then APP.
 *    Each section is clearly marked with a banner comment.
 * ============================================================
 */

// ─── React core hooks ────────────────────────────────────────
import { useState, useMemo } from "react";

// ─── MUI core components ─────────────────────────────────────
import {
    Box, Drawer, AppBar, Toolbar, Typography, IconButton,
    List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Divider, Tabs, Tab, Card, CardContent, Chip, Tooltip,
    Accordion, AccordionSummary, AccordionDetails,
    Button, useMediaQuery, CssBaseline, Badge
} from "@mui/material";

// ─── MUI theming utilities ───────────────────────────────────
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";

// ─── MUI icons ───────────────────────────────────────────────
import MenuIcon            from "@mui/icons-material/Menu";
import CloseIcon           from "@mui/icons-material/Close";
import ContentCopyIcon     from "@mui/icons-material/ContentCopy";
import CheckIcon           from "@mui/icons-material/Check";
import ExpandMoreIcon      from "@mui/icons-material/ExpandMore";
import CodeIcon            from "@mui/icons-material/Code";
import SchoolIcon          from "@mui/icons-material/School";
import FunctionsIcon       from "@mui/icons-material/Functions";
import StorageIcon         from "@mui/icons-material/Storage";
import AccountTreeIcon     from "@mui/icons-material/AccountTree";
import LinkIcon            from "@mui/icons-material/Link";
import GridOnIcon          from "@mui/icons-material/GridOn";
import ListAltIcon         from "@mui/icons-material/ListAlt";
import FilterListIcon      from "@mui/icons-material/FilterList";
import SortIcon            from "@mui/icons-material/Sort";
import SearchIcon          from "@mui/icons-material/Search";
import RepeatIcon          from "@mui/icons-material/Repeat";
import SwipeIcon           from "@mui/icons-material/Swipe";
import SpeedIcon           from "@mui/icons-material/Speed";
import BookmarkIcon        from "@mui/icons-material/Bookmark";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 1 — THEME
//  We create a MUI theme once here so every component inherits
//  the same colors, fonts and spacing automatically.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * APP_COLORS — central palette used across every component.
 * Keeping colours in one place means you only need to change
 * one line to restyle the whole app (Single Source of Truth).
 */
const APP_COLORS = {
    background:   "rgba(16, 7, 32, 0.85)", // deep purple-black
    surface:      "rgba(26, 14, 52, 0.95)", // slightly lighter card bg
    surfaceHover: "rgba(40, 20, 70, 0.95)", // hover state for cards
    border:       "rgba(218, 165, 32, 0.25)",// goldenrod border (subtle)
    borderHover:  "rgba(218, 165, 32, 0.7)", // goldenrod border on hover
    title:        "#DAA520",                // goldenrod – used for headings
    titleBright:  "#FFD700",                // gold – used for active states
    text:         "#FFFFFF",                // primary text
    textMuted:    "rgba(255,255,255,0.55)", // secondary / muted text
    success:      "#56d364",                // green for easy difficulty
    warning:      "#ffa657",                // orange for medium difficulty
    danger:       "#f85149",                // red for hard difficulty
    drawerBg:     "rgba(10, 4, 22, 0.98)", // sidebar background
    codeBg:       "rgba(8, 4, 18, 0.98)",  // code block background
};

/**
 * buildAppTheme — creates the MUI dark theme.
 *
 * Why use createTheme instead of inline styles?
 * MUI's theme system lets you set defaults ONCE and have
 * all components (Button, Card, Typography…) inherit them
 * automatically — no need to repeat sx props everywhere.
 */
const buildAppTheme = () =>
    createTheme({
        palette: {
            mode: "dark",
            background: { default: APP_COLORS.background, paper: APP_COLORS.surface },
            primary:    { main: APP_COLORS.title },
            text:       { primary: APP_COLORS.text, secondary: APP_COLORS.textMuted },
        },
        typography: {
            // JetBrains Mono for code feel; fallback to monospace
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
        },
        components: {
            // Remove default MUI Paper background override so our
            // custom rgba values show through correctly
            MuiPaper:   { styleOverrides: { root: { backgroundImage: "none" } } },
            MuiDrawer:  { styleOverrides: { paper: { backgroundImage: "none" } } },
            MuiAppBar:  { styleOverrides: { root:  { backgroundImage: "none" } } },
            MuiCard:    { styleOverrides: { root:  { backgroundImage: "none" } } },
        },
    });

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 2 — DATA LAYER
//  All Java content lives here as plain JS objects.
//  The UI components simply READ this data — they never define
//  Java code themselves. This separation means:
//    • Adding a new collection = add one object here, done.
//    • Changing UI = touch only the component layer.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * CATEGORY_STYLE_MAP — maps a method category name to a
 * display colour so chips are colour-coded consistently
 * across ALL collections without repeating the mapping.
 */
const CATEGORY_STYLE_MAP = {
    Insert:  { bg: "#0d3321", color: "#56d364" },
    Access:  { bg: "#0d2236", color: "#79c0ff" },
    Modify:  { bg: "#2d2000", color: "#e3b341" },
    Delete:  { bg: "#2d1a1a", color: "#f85149" },
    Info:    { bg: "#1e1333", color: "#bc8cff" },
    Order:   { bg: "#2a1d00", color: "#ffa657" },
    Convert: { bg: "#002a2d", color: "#39c5cf" },
};

/**
 * DIFFICULTY_COLOR_MAP — maps difficulty string to a colour.
 * Used by the practice-problem cards to colour the badge.
 */
const DIFFICULTY_COLOR_MAP = {
    Easy:   APP_COLORS.success,
    Medium: APP_COLORS.warning,
    Hard:   APP_COLORS.danger,
};

// ─── Individual collection data objects ──────────────────────
// Each collection follows the SAME shape:
// {
//   icon, color, tagline, badge, whenToUse,
//   declare, complexity, note,
//   methods: [ { name, cat, desc, code } ],
//   questions: [ { title, difficulty,
//                  solutions: [ { label, tip, code } ] } ]
// }

const ARRAY_LIST_DATA = {
    icon: "📋", color: "#f0883e",
    tagline: "Ordered list — duplicates allowed, index-based access",
    badge: "java.util.ArrayList",
    whenToUse: "When order matters, duplicates are OK, and you need fast index access",
    declare: "ArrayList<String> list = new ArrayList<>();",
    complexity: { get: "O(1)", add: "O(1)*", remove: "O(n)", search: "O(n)" },
    note: "* O(1) amortised add to end; O(n) insert at arbitrary index",
    methods: [
        { name: "add(element)",      cat: "Insert",  desc: "Appends element to the end of the list",            code: 'list.add("BMW");\n// [BMW]' },
        { name: "add(i, element)",   cat: "Insert",  desc: "Inserts element at a specific index, shifting rest", code: 'list.add(0, "Tesla");\n// [Tesla, BMW]' },
        { name: "addAll(col)",       cat: "Insert",  desc: "Appends every element from another collection",      code: "list.addAll(otherList);" },
        { name: "get(index)",        cat: "Access",  desc: "Returns the element at the given index — O(1)",      code: 'String s = list.get(0);\n// "BMW"' },
        { name: "set(index, el)",    cat: "Modify",  desc: "Replaces the element at the specified index",        code: 'list.set(0, "Ford");\n// [Ford, BMW]' },
        { name: "remove(index)",     cat: "Delete",  desc: "Removes element at the given index, shifts rest",    code: "list.remove(0); // removes first" },
        { name: "remove(obj)",       cat: "Delete",  desc: "Removes the first occurrence of this value",         code: 'list.remove("BMW");' },
        { name: "clear()",           cat: "Delete",  desc: "Empties the entire list — O(n)",                     code: "list.clear(); // []" },
        { name: "size()",            cat: "Info",    desc: "Returns the number of elements currently stored",    code: "int n = list.size();" },
        { name: "contains(el)",      cat: "Info",    desc: "Returns true if the element exists — O(n) scan",     code: 'list.contains("BMW"); // true' },
        { name: "indexOf(el)",       cat: "Info",    desc: "Returns index of first occurrence, -1 if missing",   code: 'list.indexOf("BMW"); // 0' },
        { name: "isEmpty()",         cat: "Info",    desc: "Returns true when the list has zero elements",        code: "list.isEmpty(); // false" },
        { name: "sort(comp)",        cat: "Order",   desc: "Sorts in-place using TimSort (stable)",               code: "Collections.sort(list);" },
        { name: "subList(f,t)",      cat: "Access",  desc: "Live view of elements at indices [from, to)",         code: "list.subList(1, 3);" },
        { name: "toArray()",         cat: "Convert", desc: "Returns a plain Object[] copy of the list",           code: "Object[] arr = list.toArray();" },
    ],
    questions: [
        {
            title: "Find duplicates in an ArrayList", difficulty: "Easy",
            solutions: [
                { label: "🔵 HashSet tracking",         tip: "add() returns false when the element already exists.", code: 'ArrayList<String> cars = new ArrayList<>(\n  Arrays.asList("BMW","Volvo","BMW","Ford","Volvo"));\n\nHashSet<String> seen = new HashSet<>();\nHashSet<String> dups = new HashSet<>();\n\nfor (String c : cars)\n  if (!seen.add(c)) dups.add(c);\n\nSystem.out.println(dups); // [BMW, Volvo]' },
                { label: "🟠 Collections.frequency()",  tip: "Built-in count method — simple but O(n²).", code: 'for (String c : cars)\n  if (Collections.frequency(cars, c) > 1)\n    System.out.println(c + " is a duplicate");' },
                { label: "🟢 Stream + distinct()",       tip: "Elegant Java 8+ one-liner approach.", code: 'List<String> dups = cars.stream()\n  .filter(c -> Collections.frequency(cars, c) > 1)\n  .distinct()\n  .collect(Collectors.toList());\nSystem.out.println(dups);' },
            ],
        },
        {
            title: "Reverse an ArrayList", difficulty: "Easy",
            solutions: [
                { label: "🔵 Collections.reverse()",   tip: "Single built-in call — always prefer this.",        code: 'Collections.reverse(cars);\nSystem.out.println(cars);\n// [Mazda, Ford, Volvo, BMW]' },
                { label: "🟠 Manual swap loop",         tip: "Swap elements converging from both ends.",          code: 'int l = 0, r = cars.size()-1;\nwhile (l < r) {\n  String t = cars.get(l);\n  cars.set(l++, cars.get(r));\n  cars.set(r--, t);\n}' },
                { label: "🟢 Stack (LIFO)",             tip: "Push all then pop — conceptually clean for interviews.", code: 'Stack<String> stk = new Stack<>();\nfor (String c : cars) stk.push(c);\nArrayList<String> rev = new ArrayList<>();\nwhile (!stk.isEmpty()) rev.add(stk.pop());' },
            ],
        },
        {
            title: "Remove all duplicates", difficulty: "Medium",
            solutions: [
                { label: "🔵 HashSet (fast, unordered)",      tip: "Fastest; loses insertion order.",       code: 'cars = new ArrayList<>(new HashSet<>(cars));\n// order NOT guaranteed' },
                { label: "🟠 LinkedHashSet (ordered)",         tip: "Removes dups AND keeps insertion order.", code: 'cars = new ArrayList<>(\n  new LinkedHashSet<>(cars));\n// order preserved ✅' },
                { label: "🟢 stream().distinct()",             tip: "Functional Java 8+ style.",              code: 'cars = cars.stream()\n  .distinct()\n  .collect(Collectors.toCollection(\n    ArrayList::new));' },
            ],
        },
    ],
};

const HASH_SET_DATA = {
    icon: "🔷", color: "#58a6ff",
    tagline: "Unique values — no duplicates, unordered",
    badge: "java.util.HashSet",
    whenToUse: "When uniqueness is required and order does not matter",
    declare: "HashSet<String> set = new HashSet<>();",
    complexity: { get: "O(1)", add: "O(1)", remove: "O(1)", search: "O(1)" },
    note: "Backed by a HashMap internally; all operations are average O(1)",
    methods: [
        { name: "add(element)",     cat: "Insert",  desc: "Adds element; returns false if already present",   code: 'set.add("BMW");   // true\nset.add("BMW");   // false (ignored)' },
        { name: "addAll(col)",      cat: "Insert",  desc: "Adds every element of a collection",                code: "set.addAll(otherSet);" },
        { name: "remove(el)",       cat: "Delete",  desc: "Removes the specified element; returns boolean",    code: 'set.remove("BMW"); // true' },
        { name: "clear()",          cat: "Delete",  desc: "Removes all elements from the set",                 code: "set.clear(); // {}" },
        { name: "contains(el)",     cat: "Info",    desc: "Returns true in O(1) using hash lookup",             code: 'set.contains("BMW"); // true' },
        { name: "size()",           cat: "Info",    desc: "Returns count of unique elements",                   code: "set.size(); // 3" },
        { name: "isEmpty()",        cat: "Info",    desc: "Returns true when set has zero elements",            code: "set.isEmpty(); // false" },
        { name: "iterator()",       cat: "Access",  desc: "Returns an iterator; order is NOT guaranteed",       code: 'Iterator<String> it = set.iterator();\nwhile(it.hasNext()) System.out.println(it.next());' },
        { name: "toArray()",        cat: "Convert", desc: "Copies set elements into a plain Object array",      code: "Object[] arr = set.toArray();" },
        { name: "containsAll(c)",   cat: "Info",    desc: "True if every element of c exists in this set",      code: "set.containsAll(otherSet);" },
        { name: "retainAll(c)",     cat: "Modify",  desc: "Intersection — keeps only elements in both sets",    code: "set.retainAll(otherSet); // set ∩ other" },
        { name: "removeAll(c)",     cat: "Delete",  desc: "Difference — removes every element found in c",      code: "set.removeAll(otherSet); // set - other" },
        { name: "forEach(action)",  cat: "Access",  desc: "Iterates each element with a lambda action",         code: "set.forEach(el -> System.out.println(el));" },
        { name: "stream()",         cat: "Convert", desc: "Returns a Stream for pipeline operations",            code: "set.stream().filter(e -> e.startsWith(\"B\")).forEach(System.out::println);" },
    ],
    questions: [
        {
            title: "Find common elements (intersection)", difficulty: "Easy",
            solutions: [
                { label: "🔵 retainAll()",              tip: "Built-in set intersection — one line.", code: 'HashSet<String> a = new HashSet<>(Arrays.asList("BMW","Volvo","Ford"));\nHashSet<String> b = new HashSet<>(Arrays.asList("BMW","Tesla","Ford"));\n\nHashSet<String> common = new HashSet<>(a);\ncommon.retainAll(b);\nSystem.out.println(common); // [BMW, Ford]' },
                { label: "🟠 Manual contains() loop",   tip: "Easy to read, good for interview whiteboard.", code: 'HashSet<String> common = new HashSet<>();\nfor (String s : a)\n  if (b.contains(s)) common.add(s);\nSystem.out.println(common);' },
                { label: "🟢 Stream filter",             tip: "Functional approach using method reference.", code: 'Set<String> common = a.stream()\n  .filter(b::contains)\n  .collect(Collectors.toSet());\nSystem.out.println(common);' },
            ],
        },
        {
            title: "Check if two sets are equal", difficulty: "Easy",
            solutions: [
                { label: "🔵 equals() method",           tip: "HashSet.equals() ignores order automatically.", code: 'HashSet<String> a = new HashSet<>(Arrays.asList("BMW","Ford","Volvo"));\nHashSet<String> b = new HashSet<>(Arrays.asList("Volvo","BMW","Ford"));\n\nSystem.out.println(a.equals(b)); // true' },
                { label: "🟠 containsAll() both ways",   tip: "A ⊆ B AND B ⊆ A means A = B.", code: 'boolean equal = a.containsAll(b) && b.containsAll(a);\nSystem.out.println(equal); // true' },
                { label: "🟢 Convert + sort + compare",  tip: "Useful when you need sorted output anyway.", code: 'List<String> la = new ArrayList<>(a);\nList<String> lb = new ArrayList<>(b);\nCollections.sort(la); Collections.sort(lb);\nSystem.out.println(la.equals(lb)); // true' },
            ],
        },
        {
            title: "Remove duplicates from a list using HashSet", difficulty: "Easy",
            solutions: [
                { label: "🔵 HashSet constructor",           tip: "Pass the list directly — simplest approach.", code: 'ArrayList<String> list = new ArrayList<>(\n  Arrays.asList("BMW","Volvo","BMW","Ford"));\n\nHashSet<String> unique = new HashSet<>(list);\nSystem.out.println(unique);' },
                { label: "🟠 LinkedHashSet (preserves order)", tip: "Same speed but keeps insertion order.", code: 'LinkedHashSet<String> unique = new LinkedHashSet<>(list);\nSystem.out.println(unique); // order preserved' },
                { label: "🟢 Stream + toSet()",              tip: "Stream approach with Collectors.", code: 'Set<String> unique = list.stream()\n  .collect(Collectors.toSet());\nSystem.out.println(unique);' },
            ],
        },
    ],
};

const HASH_MAP_DATA = {
    icon: "🗺️", color: "#7ee787",
    tagline: "Key → Value pairs — O(1) lookup by key",
    badge: "java.util.HashMap",
    whenToUse: "When you need to associate unique keys with values (like a dictionary)",
    declare: "HashMap<String, Integer> map = new HashMap<>();",
    complexity: { get: "O(1)", add: "O(1)", remove: "O(1)", search: "O(1)" },
    note: "Average O(1) due to hashing; degrades to O(n) on hash collisions",
    methods: [
        { name: "put(key, val)",       cat: "Insert",  desc: "Adds or overwrites a key-value mapping",              code: 'map.put("BMW", 2020);\nmap.put("BMW", 2023); // overwrites' },
        { name: "putIfAbsent(k,v)",    cat: "Insert",  desc: "Inserts only if key does not already exist",           code: 'map.putIfAbsent("Ford", 2021);' },
        { name: "putAll(map)",         cat: "Insert",  desc: "Copies all mappings from another map",                 code: "map.putAll(otherMap);" },
        { name: "get(key)",            cat: "Access",  desc: "Returns value for key, or null if absent",             code: 'int year = map.get("BMW"); // 2023' },
        { name: "getOrDefault(k,d)",   cat: "Access",  desc: "Returns value or the given default if key missing",    code: 'map.getOrDefault("Tesla", 0); // 0' },
        { name: "remove(key)",         cat: "Delete",  desc: "Removes and returns the value for this key",           code: 'map.remove("Ford");' },
        { name: "clear()",             cat: "Delete",  desc: "Removes every key-value pair from the map",            code: "map.clear();" },
        { name: "containsKey(k)",      cat: "Info",    desc: "True if key exists — O(1) hash lookup",                code: 'map.containsKey("BMW"); // true' },
        { name: "containsValue(v)",    cat: "Info",    desc: "True if any key maps to this value — O(n) scan",       code: "map.containsValue(2023); // true" },
        { name: "size()",              cat: "Info",    desc: "Returns the number of key-value mappings",              code: "map.size(); // 3" },
        { name: "isEmpty()",           cat: "Info",    desc: "Returns true when no mappings exist",                   code: "map.isEmpty(); // false" },
        { name: "keySet()",            cat: "Access",  desc: "Returns a Set view of all keys",                       code: "Set<String> keys = map.keySet();" },
        { name: "values()",            cat: "Access",  desc: "Returns a Collection of all values",                   code: "Collection<Integer> vals = map.values();" },
        { name: "entrySet()",          cat: "Access",  desc: "Returns Set of Map.Entry pairs for iteration",          code: 'for (Map.Entry<String,Integer> e : map.entrySet())\n  System.out.println(e.getKey()+":"+e.getValue());' },
        { name: "replace(key, val)",   cat: "Modify",  desc: "Replaces the value only if key already exists",         code: 'map.replace("BMW", 2024);' },
        { name: "merge(k,v,fn)",       cat: "Modify",  desc: "Applies BiFunction to merge new value with old",        code: 'map.merge("BMW", 1, Integer::sum);' },
        { name: "forEach(action)",     cat: "Access",  desc: "Iterates all entries with a BiConsumer lambda",          code: 'map.forEach((k,v) -> System.out.println(k+"="+v));' },
    ],
    questions: [
        {
            title: "Count frequency of each element", difficulty: "Easy",
            solutions: [
                { label: "🔵 getOrDefault()",            tip: "Clean and idiomatic — most common interview answer.", code: 'String[] words = {"apple","banana","apple","cherry","banana","apple"};\nHashMap<String, Integer> freq = new HashMap<>();\n\nfor (String w : words)\n  freq.put(w, freq.getOrDefault(w, 0) + 1);\n\nSystem.out.println(freq); // {apple=3, banana=2, cherry=1}' },
                { label: "🟠 merge() method",            tip: "One-liner update — avoids null check entirely.", code: 'for (String w : words)\n  freq.merge(w, 1, Integer::sum);\n\nSystem.out.println(freq);' },
                { label: "🟢 Collectors.groupingBy()",   tip: "Stream + counting collector — very expressive.", code: 'Map<String,Long> freq = Arrays.stream(words)\n  .collect(Collectors.groupingBy(\n    w -> w, Collectors.counting()));\n\nSystem.out.println(freq);' },
            ],
        },
        {
            title: "Find the key with the maximum value", difficulty: "Medium",
            solutions: [
                { label: "🔵 Manual loop",               tip: "Crystal clear logic — great for interviews.", code: 'String topKey = null;\nint maxVal = Integer.MIN_VALUE;\n\nfor (Map.Entry<String,Integer> e : scores.entrySet())\n  if (e.getValue() > maxVal) {\n    maxVal = e.getValue();\n    topKey = e.getKey();\n  }\n\nSystem.out.println(topKey);' },
                { label: "🟠 Collections.max()",         tip: "Use Entry comparator — no loop needed.", code: 'Map.Entry<String,Integer> max =\n  Collections.max(\n    scores.entrySet(),\n    Map.Entry.comparingByValue());\n\nSystem.out.println(max.getKey());' },
                { label: "🟢 Stream + max()",            tip: "Functional pipeline — most concise.", code: 'String top = scores.entrySet().stream()\n  .max(Map.Entry.comparingByValue())\n  .map(Map.Entry::getKey)\n  .orElse("none");\n\nSystem.out.println(top);' },
            ],
        },
        {
            title: "Group a list by a property", difficulty: "Medium",
            solutions: [
                { label: "🔵 Manual putIfAbsent loop",  tip: "Classic approach — easy to understand step by step.", code: 'String[] cars = {"BMW","Buick","Ford","Volvo","Fiat","VW"};\nHashMap<Character, List<String>> grouped = new HashMap<>();\n\nfor (String c : cars) {\n  char key = c.charAt(0);\n  grouped.putIfAbsent(key, new ArrayList<>());\n  grouped.get(key).add(c);\n}' },
                { label: "🟠 computeIfAbsent()",        tip: "Creates the list lazily — cleaner than putIfAbsent.", code: 'for (String c : cars)\n  grouped.computeIfAbsent(\n    c.charAt(0), k -> new ArrayList<>()\n  ).add(c);' },
                { label: "🟢 Collectors.groupingBy()",  tip: "Stream one-liner — most expressive.", code: 'Map<Character, List<String>> grouped =\n  Arrays.stream(cars)\n    .collect(Collectors.groupingBy(\n      c -> c.charAt(0)));\n\nSystem.out.println(grouped);' },
            ],
        },
    ],
};

const LINKED_LIST_DATA = {
    icon: "🔗", color: "#e879f9",
    tagline: "Doubly-linked list — fast O(1) insert/delete at ends",
    badge: "java.util.LinkedList",
    whenToUse: "Queue / Stack / Deque operations; frequent add/remove at head or tail",
    declare: "LinkedList<String> ll = new LinkedList<>();",
    complexity: { get: "O(n)", add: "O(1)*", remove: "O(1)*", search: "O(n)" },
    note: "* O(1) only at head/tail; O(n) at an arbitrary index",
    methods: [
        { name: "add(element)",          cat: "Insert",  desc: "Appends to the end of the list",                      code: 'll.add("B"); // [B]' },
        { name: "addFirst(el)",          cat: "Insert",  desc: "Inserts at the head — always O(1)",                    code: 'll.addFirst("A"); // [A, B]' },
        { name: "addLast(el)",           cat: "Insert",  desc: "Appends to the tail — always O(1)",                    code: 'll.addLast("C"); // [A, B, C]' },
        { name: "offer(el)",             cat: "Insert",  desc: "Queue enqueue — adds to tail, returns boolean",        code: "ll.offer(\"D\"); // queue-style add" },
        { name: "push(el)",              cat: "Insert",  desc: "Stack push — equivalent to addFirst()",                code: "ll.push(\"X\"); // like addFirst" },
        { name: "get(index)",            cat: "Access",  desc: "Returns element at index — must traverse: O(n)",       code: 'String s = ll.get(1); // "B"' },
        { name: "getFirst()",            cat: "Access",  desc: "Returns head element without removing — O(1)",         code: 'String first = ll.getFirst();' },
        { name: "getLast()",             cat: "Access",  desc: "Returns tail element without removing — O(1)",         code: 'String last = ll.getLast();' },
        { name: "peek()",                cat: "Access",  desc: "Queue peek — returns head or null, no removal",        code: "String h = ll.peek();" },
        { name: "peekFirst()",           cat: "Access",  desc: "Returns first element or null if empty",               code: "ll.peekFirst();" },
        { name: "peekLast()",            cat: "Access",  desc: "Returns last element or null if empty",                code: "ll.peekLast();" },
        { name: "poll()",                cat: "Delete",  desc: "Queue dequeue — removes and returns head or null",     code: "String h = ll.poll();" },
        { name: "pollFirst()",           cat: "Delete",  desc: "Removes and returns first element",                    code: "ll.pollFirst();" },
        { name: "pollLast()",            cat: "Delete",  desc: "Removes and returns last element",                     code: "ll.pollLast();" },
        { name: "pop()",                 cat: "Delete",  desc: "Stack pop — removes and returns head",                 code: "String top = ll.pop();" },
        { name: "removeFirst()",         cat: "Delete",  desc: "Removes and returns first — throws if empty",          code: "ll.removeFirst();" },
        { name: "removeLast()",          cat: "Delete",  desc: "Removes and returns last — throws if empty",           code: "ll.removeLast();" },
        { name: "size()",                cat: "Info",    desc: "Returns the number of elements stored",                code: "int n = ll.size();" },
        { name: "contains(el)",          cat: "Info",    desc: "Linear O(n) scan for the element",                     code: 'll.contains("B"); // true' },
        { name: "descendingIterator()",  cat: "Access",  desc: "Returns an iterator traversing from tail to head",     code: "Iterator<String> it = ll.descendingIterator();" },
        { name: "clear()",               cat: "Delete",  desc: "Removes all elements from the list",                   code: "ll.clear();" },
    ],
    questions: [
        {
            title: "Implement a Queue using LinkedList", difficulty: "Easy",
            solutions: [
                { label: "🔵 offer() + poll()",         tip: "Standard Queue interface — offer/poll never throw.", code: 'Queue<String> queue = new LinkedList<>();\n\nqueue.offer("Alice");\nqueue.offer("Bob");\nqueue.offer("Charlie");\n\nSystem.out.println(queue.peek());  // Alice\n\nString served = queue.poll(); // Alice\nSystem.out.println("Next: " + queue.peek()); // Bob' },
                { label: "🟠 add() + remove()",         tip: "Throws NoSuchElementException if empty — use with care.", code: 'Queue<String> queue = new LinkedList<>();\nqueue.add("Alice");\nqueue.add("Bob");\n\nString next = queue.remove(); // Alice — throws if empty\nSystem.out.println(queue.element()); // Bob — peek no-remove' },
                { label: "🟢 ArrayDeque (preferred)",    tip: "ArrayDeque is faster than LinkedList for queues.", code: 'Deque<String> queue = new ArrayDeque<>();\nqueue.offerLast("Alice");\nqueue.offerLast("Bob");\nqueue.offerLast("Charlie");\n\nwhile (!queue.isEmpty())\n  System.out.println(queue.pollFirst());\n// Alice → Bob → Charlie' },
            ],
        },
        {
            title: "Implement a Stack using LinkedList", difficulty: "Easy",
            solutions: [
                { label: "🔵 push() + pop()",           tip: "LinkedList has built-in stack semantics.", code: 'LinkedList<Integer> stack = new LinkedList<>();\n\nstack.push(10); // [10]\nstack.push(20); // [20, 10]\nstack.push(30); // [30, 20, 10]\n\nSystem.out.println(stack.peek()); // 30\nSystem.out.println(stack.pop());  // 30\nSystem.out.println(stack);        // [20, 10]' },
                { label: "🟠 addFirst() + removeFirst()", tip: "Explicit about which end acts as the top.", code: 'LinkedList<Integer> stack = new LinkedList<>();\nstack.addFirst(10);\nstack.addFirst(20);\n\nint top = stack.removeFirst(); // 20\nSystem.out.println(top);\nSystem.out.println(stack); // [10]' },
                { label: "🟢 Deque interface",           tip: "Deque is the recommended Stack replacement in Java.", code: 'Deque<Integer> stack = new LinkedList<>();\nstack.offerFirst(10);\nstack.offerFirst(20);\nstack.offerFirst(30);\n\nSystem.out.println(stack.peekFirst()); // 30\nstack.pollFirst(); // pop\nSystem.out.println(stack); // [20, 10]' },
            ],
        },
        {
            title: "Reverse a LinkedList", difficulty: "Medium",
            solutions: [
                { label: "🔵 Collections.reverse()",    tip: "One-liner — uses the built-in swap algorithm.", code: 'LinkedList<Integer> ll = new LinkedList<>(\n  Arrays.asList(1, 2, 3, 4, 5));\n\nCollections.reverse(ll);\nSystem.out.println(ll); // [5, 4, 3, 2, 1]' },
                { label: "🟠 descendingIterator()",     tip: "Walks backwards without copying.", code: 'LinkedList<Integer> rev = new LinkedList<>();\nIterator<Integer> it = ll.descendingIterator();\nwhile (it.hasNext()) rev.add(it.next());\n\nSystem.out.println(rev); // [5, 4, 3, 2, 1]' },
                { label: "🟢 Stack-based reversal",     tip: "Push all then pop — good interview answer.", code: 'Stack<Integer> stk = new Stack<>();\nfor (int n : ll) stk.push(n);\n\nLinkedList<Integer> rev = new LinkedList<>();\nwhile (!stk.isEmpty()) rev.add(stk.pop());\n\nSystem.out.println(rev); // [5, 4, 3, 2, 1]' },
            ],
        },
    ],
};

const TREE_MAP_DATA = {
    icon: "🌲", color: "#34d399",
    tagline: "Sorted key→value map — keys in natural ascending order",
    badge: "java.util.TreeMap",
    whenToUse: "Need a sorted map or range/floor/ceiling key queries",
    declare: "TreeMap<String, Integer> tm = new TreeMap<>();",
    complexity: { get: "O(log n)", add: "O(log n)", remove: "O(log n)", search: "O(log n)" },
    note: "Backed by a Red-Black Tree — all operations O(log n)",
    methods: [
        { name: "put(key, val)",       cat: "Insert",  desc: "Inserts mapping; keys are auto-sorted on insert",    code: 'tm.put("Banana", 2);\ntm.put("Apple", 1);\n// {Apple=1, Banana=2}' },
        { name: "get(key)",            cat: "Access",  desc: "Returns value for key, or null",                     code: 'int v = tm.get("Apple"); // 1' },
        { name: "firstKey()",          cat: "Access",  desc: "Returns the lowest (first) key in sorted order",     code: 'String k = tm.firstKey(); // "Apple"' },
        { name: "lastKey()",           cat: "Access",  desc: "Returns the highest (last) key in sorted order",     code: 'String k = tm.lastKey();' },
        { name: "floorKey(key)",       cat: "Access",  desc: "Greatest key that is ≤ the given key",               code: 'tm.floorKey("Cherry"); // "Banana"' },
        { name: "ceilingKey(key)",     cat: "Access",  desc: "Smallest key that is ≥ the given key",               code: 'tm.ceilingKey("Cherry");' },
        { name: "higherKey(key)",      cat: "Access",  desc: "Smallest key strictly greater than given key",       code: 'tm.higherKey("Banana");' },
        { name: "lowerKey(key)",       cat: "Access",  desc: "Greatest key strictly less than given key",          code: 'tm.lowerKey("Mango");' },
        { name: "headMap(toKey)",      cat: "Access",  desc: "Live view of all entries with key < toKey",          code: 'tm.headMap("Mango"); // {Apple, Banana}' },
        { name: "tailMap(fromKey)",    cat: "Access",  desc: "Live view of all entries with key ≥ fromKey",        code: 'tm.tailMap("Banana");' },
        { name: "subMap(from,to)",     cat: "Access",  desc: "Live view for keys in range [from, to)",             code: 'tm.subMap("Apple","Mango");' },
        { name: "remove(key)",         cat: "Delete",  desc: "Removes the mapping for the specified key",          code: 'tm.remove("Apple");' },
        { name: "containsKey(k)",      cat: "Info",    desc: "Returns true if key exists — O(log n)",              code: 'tm.containsKey("Banana"); // true' },
        { name: "size()",              cat: "Info",    desc: "Returns number of key-value mappings",               code: "tm.size();" },
        { name: "keySet()",            cat: "Access",  desc: "Returns keys as a sorted NavigableSet",              code: "Set<String> keys = tm.keySet();" },
        { name: "entrySet()",          cat: "Access",  desc: "Returns sorted set of Map.Entry objects",            code: 'for (var e : tm.entrySet())\n  System.out.println(e.getKey()+"="+e.getValue());' },
        { name: "descendingMap()",     cat: "Access",  desc: "Returns a reverse-order view of the entire map",     code: "NavigableMap<String,Integer> desc = tm.descendingMap();" },
    ],
    questions: [
        {
            title: "Find the closest key to a target", difficulty: "Medium",
            solutions: [
                { label: "🔵 floorKey() + ceilingKey()", tip: "Best O(log n) built-in navigation approach.", code: 'TreeMap<Integer, String> events = new TreeMap<>();\nevents.put(10, "Meeting");\nevents.put(20, "Lunch");\nevents.put(35, "Review");\nevents.put(50, "Standup");\n\nint target = 25;\nInteger floor   = events.floorKey(target);   // 20\nInteger ceiling = events.ceilingKey(target); // 35\n\nSystem.out.println("Before: " + floor + " → " + events.get(floor));\nSystem.out.println("After:  " + ceiling + " → " + events.get(ceiling));' },
                { label: "🟠 headMap + tailMap",          tip: "Get sub-ranges, then inspect boundaries.", code: 'SortedMap<Integer,String> head = events.headMap(target + 1);\nInteger floor = head.isEmpty() ? null : head.lastKey();\n\nSortedMap<Integer,String> tail = events.tailMap(target);\nInteger ceiling = tail.isEmpty() ? null : tail.firstKey();\n\nSystem.out.println("Floor: " + floor + ", Ceiling: " + ceiling);' },
                { label: "🟢 Manual distance loop",       tip: "Compare distances — finds nearest regardless of direction.", code: 'int target = 25, closest = -1;\nint minDist = Integer.MAX_VALUE;\n\nfor (int key : events.keySet()) {\n  int dist = Math.abs(key - target);\n  if (dist < minDist) { minDist = dist; closest = key; }\n}\nSystem.out.println("Closest key: " + closest); // 20' },
            ],
        },
        {
            title: "Sort a map by key (ascending & descending)", difficulty: "Easy",
            solutions: [
                { label: "🔵 TreeMap natural order",       tip: "TreeMap sorts keys ascending by default.", code: 'HashMap<String, Integer> scores = new HashMap<>();\nscores.put("Charlie", 85);\nscores.put("Alice", 92);\nscores.put("Bob", 78);\n\nTreeMap<String, Integer> sorted = new TreeMap<>(scores);\nSystem.out.println(sorted);\n// {Alice=92, Bob=78, Charlie=85}' },
                { label: "🟠 TreeMap reverseOrder()",      tip: "Pass Comparator.reverseOrder() for descending.", code: 'TreeMap<String, Integer> desc =\n  new TreeMap<>(Collections.reverseOrder());\ndesc.putAll(scores);\nSystem.out.println(desc);\n// {Charlie=85, Bob=78, Alice=92}' },
                { label: "🟢 Stream sorted()",             tip: "Sort entries via stream — does not create TreeMap.", code: 'scores.entrySet().stream()\n  .sorted(Map.Entry.comparingByKey())\n  .forEach(e ->\n    System.out.println(e.getKey() + "=" + e.getValue()));' },
            ],
        },
        {
            title: "Count words and sort alphabetically", difficulty: "Medium",
            solutions: [
                { label: "🔵 TreeMap + getOrDefault()",    tip: "TreeMap auto-sorts; getOrDefault counts cleanly.", code: 'String[] words = {"banana","apple","cherry","apple","banana","apple"};\nTreeMap<String, Integer> freq = new TreeMap<>();\n\nfor (String w : words)\n  freq.put(w, freq.getOrDefault(w, 0) + 1);\n\nSystem.out.println(freq);\n// {apple=3, banana=2, cherry=1}' },
                { label: "🟠 TreeMap + merge()",           tip: "merge() is cleaner — no null check needed.", code: 'TreeMap<String, Integer> freq = new TreeMap<>();\nfor (String w : words)\n  freq.merge(w, 1, Integer::sum);\n\nfreq.forEach((k, v) -> System.out.println(k + ": " + v));' },
                { label: "🟢 Stream + groupingBy + TreeMap", tip: "Collect directly into a sorted map.", code: 'Map<String, Long> freq = Arrays.stream(words)\n  .collect(Collectors.groupingBy(\n    w -> w,\n    TreeMap::new,\n    Collectors.counting()));\n\nSystem.out.println(freq);' },
            ],
        },
    ],
};

const TREE_SET_DATA = {
    icon: "🌳", color: "#a78bfa",
    tagline: "Sorted unique elements — no duplicates, natural order",
    badge: "java.util.TreeSet",
    whenToUse: "Need sorted unique values with O(log n) range / navigation operations",
    declare: "TreeSet<Integer> ts = new TreeSet<>();",
    complexity: { get: "O(log n)", add: "O(log n)", remove: "O(log n)", search: "O(log n)" },
    note: "Backed by TreeMap internally; implements NavigableSet",
    methods: [
        { name: "add(element)",      cat: "Insert",  desc: "Adds element; auto-sorted, duplicate silently ignored", code: "ts.add(5); ts.add(2); ts.add(8);\n// [2, 5, 8]" },
        { name: "addAll(col)",       cat: "Insert",  desc: "Adds all elements from a collection",                    code: "ts.addAll(Arrays.asList(1, 3, 7));" },
        { name: "first()",           cat: "Access",  desc: "Returns the lowest element in the set",                  code: "ts.first(); // 2" },
        { name: "last()",            cat: "Access",  desc: "Returns the highest element in the set",                 code: "ts.last(); // 8" },
        { name: "floor(el)",         cat: "Access",  desc: "Greatest element ≤ given value",                         code: "ts.floor(6); // 5" },
        { name: "ceiling(el)",       cat: "Access",  desc: "Smallest element ≥ given value",                         code: "ts.ceiling(6); // 8" },
        { name: "higher(el)",        cat: "Access",  desc: "Smallest element strictly greater than given value",      code: "ts.higher(5); // 8" },
        { name: "lower(el)",         cat: "Access",  desc: "Greatest element strictly less than given value",         code: "ts.lower(5); // 2" },
        { name: "headSet(to)",       cat: "Access",  desc: "Live view of elements strictly less than to",            code: "ts.headSet(6); // [2, 5]" },
        { name: "tailSet(from)",     cat: "Access",  desc: "Live view of elements greater than or equal to from",    code: "ts.tailSet(5); // [5, 8]" },
        { name: "subSet(f,t)",       cat: "Access",  desc: "Live view for elements in range [from, to)",             code: "ts.subSet(2, 8); // [2, 5]" },
        { name: "remove(el)",        cat: "Delete",  desc: "Removes the specified element",                           code: "ts.remove(5); // [2, 8]" },
        { name: "pollFirst()",       cat: "Delete",  desc: "Removes and returns the lowest element",                  code: "int min = ts.pollFirst(); // 2" },
        { name: "pollLast()",        cat: "Delete",  desc: "Removes and returns the highest element",                 code: "int max = ts.pollLast(); // 8" },
        { name: "contains(el)",      cat: "Info",    desc: "True if element exists — O(log n) binary search",        code: "ts.contains(5); // true" },
        { name: "size()",            cat: "Info",    desc: "Returns number of elements",                              code: "ts.size(); // 3" },
        { name: "descendingSet()",   cat: "Access",  desc: "Returns a reverse-order view of this set",               code: "NavigableSet<Integer> desc = ts.descendingSet();" },
    ],
    questions: [
        {
            title: "Find kth smallest element", difficulty: "Medium",
            solutions: [
                { label: "🔵 TreeSet iteration",         tip: "TreeSet is already sorted — iterate k steps.", code: 'int[] nums = {7, 2, 15, 1, 9, 4, 11};\nTreeSet<Integer> ts = new TreeSet<>();\nfor (int n : nums) ts.add(n);\n// ts = [1, 2, 4, 7, 9, 11, 15]\n\nint k = 3;\nIterator<Integer> it = ts.iterator();\nint result = -1;\nfor (int i = 0; i < k; i++) result = it.next();\n\nSystem.out.println(k + "rd smallest: " + result); // 4' },
                { label: "🟠 Convert to ArrayList",      tip: "Convert to list then use get(k-1) directly.", code: 'ArrayList<Integer> sorted = new ArrayList<>(ts);\n// [1, 2, 4, 7, 9, 11, 15]\n\nint k = 3;\nSystem.out.println(k + "rd smallest: " + sorted.get(k-1)); // 4' },
                { label: "🟢 Stream skip + findFirst()", tip: "Skip k-1 elements, take the very next one.", code: 'int k = 3;\nint result = ts.stream()\n  .skip(k - 1)\n  .findFirst()\n  .orElse(-1);\n\nSystem.out.println(k + "rd smallest: " + result); // 4' },
            ],
        },
        {
            title: "Find elements in a range", difficulty: "Easy",
            solutions: [
                { label: "🔵 subSet(from, to)",          tip: "Built-in O(log n) inclusive range query.", code: 'TreeSet<Integer> ts = new TreeSet<>(\n  Arrays.asList(3, 7, 12, 18, 25, 31, 40));\n\n// Elements in [10, 30] inclusive on both ends\nNavigableSet<Integer> range = ts.subSet(10, true, 30, true);\nSystem.out.println(range); // [12, 18, 25]' },
                { label: "🟠 headSet + tailSet",          tip: "Intersect two live views for range.", code: '// Elements >= 10 AND <= 30\nSortedSet<Integer> range =\n  ts.tailSet(10).headSet(31);\nSystem.out.println(range); // [12, 18, 25]' },
                { label: "🟢 Stream filter",              tip: "Readable but O(n) — fine for small sets.", code: 'List<Integer> range = ts.stream()\n  .filter(n -> n >= 10 && n <= 30)\n  .collect(Collectors.toList());\nSystem.out.println(range); // [12, 18, 25]' },
            ],
        },
        {
            title: "Remove duplicates and sort in one step", difficulty: "Easy",
            solutions: [
                { label: "🔵 TreeSet constructor",        tip: "Pass any collection — sorted + unique instantly.", code: 'ArrayList<Integer> nums = new ArrayList<>(\n  Arrays.asList(5, 2, 8, 2, 1, 5, 9, 1));\n\nTreeSet<Integer> result = new TreeSet<>(nums);\nSystem.out.println(result); // [1, 2, 5, 8, 9]' },
                { label: "🟠 addAll()",                   tip: "Start empty then bulk-add.", code: 'TreeSet<Integer> result = new TreeSet<>();\nresult.addAll(nums);\nSystem.out.println(result); // [1, 2, 5, 8, 9]' },
                { label: "🟢 Stream distinct + sorted",   tip: "Explicit pipeline — reads like English.", code: 'List<Integer> result = nums.stream()\n  .distinct()\n  .sorted()\n  .collect(Collectors.toList());\nSystem.out.println(result); // [1, 2, 5, 8, 9]' },
            ],
        },
    ],
};

const LINKED_HASH_SET_DATA = {
    icon: "🔗🔷", color: "#fb923c",
    tagline: "Unique elements — insertion order preserved",
    badge: "java.util.LinkedHashSet",
    whenToUse: "Need uniqueness like HashSet but want predictable iteration order",
    declare: "LinkedHashSet<String> lhs = new LinkedHashSet<>();",
    complexity: { get: "O(1)", add: "O(1)", remove: "O(1)", search: "O(1)" },
    note: "HashSet + doubly-linked list tracking insertion order",
    methods: [
        { name: "add(element)",      cat: "Insert",  desc: "Adds element; preserves insertion order, ignores dups", code: 'lhs.add("Banana");\nlhs.add("Apple");\nlhs.add("Banana"); // ignored\n// [Banana, Apple]' },
        { name: "addAll(col)",       cat: "Insert",  desc: "Adds all, preserving order of source collection",        code: 'lhs.addAll(Arrays.asList("Cherry","Date"));' },
        { name: "remove(el)",        cat: "Delete",  desc: "Removes element; rest of order is maintained",           code: 'lhs.remove("Apple");' },
        { name: "contains(el)",      cat: "Info",    desc: "True if element exists — O(1)",                          code: 'lhs.contains("Banana"); // true' },
        { name: "size()",            cat: "Info",    desc: "Returns number of unique elements",                       code: "lhs.size(); // 2" },
        { name: "isEmpty()",         cat: "Info",    desc: "True when no elements",                                   code: "lhs.isEmpty(); // false" },
        { name: "clear()",           cat: "Delete",  desc: "Removes all elements",                                    code: "lhs.clear();" },
        { name: "iterator()",        cat: "Access",  desc: "Iterates in insertion order — guaranteed",                code: "for (String s : lhs) System.out.println(s);" },
        { name: "toArray()",         cat: "Convert", desc: "Array of elements in insertion order",                    code: "Object[] arr = lhs.toArray();" },
        { name: "retainAll(col)",    cat: "Modify",  desc: "Intersection — keeps only elements in both",              code: "lhs.retainAll(otherSet);" },
        { name: "removeAll(col)",    cat: "Delete",  desc: "Difference — removes all elements in col",                code: "lhs.removeAll(otherSet);" },
        { name: "containsAll(col)",  cat: "Info",    desc: "True if every element in col is in this set",             code: "lhs.containsAll(otherSet);" },
        { name: "forEach(action)",   cat: "Access",  desc: "Executes action for each element in order",               code: "lhs.forEach(System.out::println);" },
        { name: "stream()",          cat: "Convert", desc: "Stream in insertion order",                               code: 'lhs.stream().filter(s -> s.length() > 4).forEach(System.out::println);' },
    ],
    questions: [
        {
            title: "Remove duplicates, preserve insertion order", difficulty: "Easy",
            solutions: [
                { label: "🔵 LinkedHashSet constructor", tip: "Pass list directly — O(n), order preserved.", code: 'List<String> fruits = Arrays.asList(\n  "Banana","Apple","Cherry","Apple","Banana","Date");\n\nLinkedHashSet<String> unique = new LinkedHashSet<>(fruits);\nSystem.out.println(unique);\n// [Banana, Apple, Cherry, Date] ✅' },
                { label: "🟠 Manual add loop",           tip: "Gives you control over what you skip.", code: 'LinkedHashSet<String> unique = new LinkedHashSet<>();\nfor (String f : fruits) {\n  boolean added = unique.add(f);\n  if (!added)\n    System.out.println("Skipped: " + f);\n}\nSystem.out.println(unique);' },
                { label: "🟢 Stream + LinkedHashSet",    tip: "Functional style; collector preserves order.", code: 'LinkedHashSet<String> unique = fruits.stream()\n  .collect(Collectors.toCollection(\n    LinkedHashSet::new));\nSystem.out.println(unique);' },
            ],
        },
        {
            title: "Find first non-repeating element", difficulty: "Medium",
            solutions: [
                { label: "🔵 LinkedHashSet + HashSet",   tip: "Track seen and duplicates in two sets; subtract.", code: 'List<String> items = Arrays.asList("a","b","c","a","b","d");\n\nLinkedHashSet<String> seen  = new LinkedHashSet<>();\nHashSet<String>       dupes = new HashSet<>();\n\nfor (String s : items)\n  if (!seen.add(s)) dupes.add(s);\n\nseen.removeAll(dupes);\nSystem.out.println(seen.iterator().next()); // "c"' },
                { label: "🟠 LinkedHashMap count first",  tip: "Count in order then pick first with count=1.", code: 'LinkedHashMap<String, Integer> count = new LinkedHashMap<>();\nfor (String s : items)\n  count.merge(s, 1, Integer::sum);\n\nString first = count.entrySet().stream()\n  .filter(e -> e.getValue() == 1)\n  .map(Map.Entry::getKey)\n  .findFirst().orElse(null);\nSystem.out.println(first); // "c"' },
                { label: "🟢 Stream filter",              tip: "One-liner — O(n²) but concise.", code: 'String first = items.stream()\n  .filter(s -> Collections.frequency(items, s) == 1)\n  .findFirst().orElse(null);\nSystem.out.println(first); // "c"' },
            ],
        },
        {
            title: "Set operations preserving insertion order", difficulty: "Medium",
            solutions: [
                { label: "🔵 Union with addAll()",       tip: "Union: combine both sets; order of A then new-in-B.", code: 'LinkedHashSet<String> a = new LinkedHashSet<>(Arrays.asList("Apple","Banana","Cherry"));\nLinkedHashSet<String> b = new LinkedHashSet<>(Arrays.asList("Banana","Date","Apple","Elderberry"));\n\nLinkedHashSet<String> union = new LinkedHashSet<>(a);\nunion.addAll(b);\nSystem.out.println("Union: " + union);\n// [Apple, Banana, Cherry, Date, Elderberry]' },
                { label: "🟠 Intersection retainAll()",  tip: "Intersection: only elements in both, in A\'s order.", code: 'LinkedHashSet<String> inter = new LinkedHashSet<>(a);\ninter.retainAll(b);\nSystem.out.println("Intersection: " + inter);\n// [Apple, Banana]' },
                { label: "🟢 Difference removeAll()",    tip: "Difference: elements in A but NOT in B.", code: 'LinkedHashSet<String> diff = new LinkedHashSet<>(a);\ndiff.removeAll(b);\nSystem.out.println("Difference (A−B): " + diff);\n// [Cherry]' },
            ],
        },
    ],
};

const LINKED_HASH_MAP_DATA = {
    icon: "🗂️", color: "#f472b6",
    tagline: "Key→Value pairs — insertion order preserved",
    badge: "java.util.LinkedHashMap",
    whenToUse: "Ordered map; access-order mode enables LRU Cache implementation",
    declare: "LinkedHashMap<String, Integer> lhm = new LinkedHashMap<>();",
    complexity: { get: "O(1)", add: "O(1)", remove: "O(1)", search: "O(1)" },
    note: "HashMap + doubly-linked list. Pass true for access-order LRU mode",
    methods: [
        { name: "put(key, val)",       cat: "Insert",  desc: "Inserts key-value; maintains insertion order",          code: 'lhm.put("One", 1);\nlhm.put("Two", 2);\nlhm.put("Three", 3);\n// {One=1, Two=2, Three=3}' },
        { name: "get(key)",            cat: "Access",  desc: "Returns value; in access-order mode, moves to tail",    code: 'int v = lhm.get("Two"); // 2' },
        { name: "getOrDefault(k,d)",   cat: "Access",  desc: "Returns value or default if key missing",               code: 'lhm.getOrDefault("Four", 0); // 0' },
        { name: "putIfAbsent(k,v)",    cat: "Insert",  desc: "Inserts only if key is absent",                         code: 'lhm.putIfAbsent("One", 99); // ignored' },
        { name: "remove(key)",         cat: "Delete",  desc: "Removes the mapping for the specified key",             code: 'lhm.remove("Two");' },
        { name: "containsKey(k)",      cat: "Info",    desc: "True if key exists — O(1)",                             code: 'lhm.containsKey("One"); // true' },
        { name: "containsValue(v)",    cat: "Info",    desc: "True if any key maps to this value",                    code: "lhm.containsValue(1); // true" },
        { name: "size()",              cat: "Info",    desc: "Number of key-value pairs",                              code: "lhm.size();" },
        { name: "isEmpty()",           cat: "Info",    desc: "True when no mappings exist",                            code: "lhm.isEmpty(); // false" },
        { name: "keySet()",            cat: "Access",  desc: "Keys in insertion order",                               code: "Set<String> keys = lhm.keySet();" },
        { name: "values()",            cat: "Access",  desc: "Values in insertion order",                             code: "Collection<Integer> vals = lhm.values();" },
        { name: "entrySet()",          cat: "Access",  desc: "Entries in insertion order for iteration",              code: 'for (var e : lhm.entrySet())\n  System.out.println(e.getKey()+"="+e.getValue());' },
        { name: "forEach(action)",     cat: "Access",  desc: "Iterate all pairs in insertion order",                  code: 'lhm.forEach((k,v) -> System.out.println(k+":"+v));' },
        { name: "replace(k, v)",       cat: "Modify",  desc: "Replaces value only if key exists",                     code: 'lhm.replace("One", 100);' },
        { name: "merge(k,v,fn)",       cat: "Modify",  desc: "Merges or updates value with BiFunction",               code: 'lhm.merge("One", 1, Integer::sum);' },
        { name: "clear()",             cat: "Delete",  desc: "Removes all mappings",                                   code: "lhm.clear();" },
        { name: "removeEldestEntry()", cat: "Modify",  desc: "Override in subclass to evict oldest entry (LRU)",      code: '// Override in anonymous subclass:\nprotected boolean removeEldestEntry(Map.Entry e) {\n  return size() > MAX_SIZE;\n}' },
    ],
    questions: [
        {
            title: "Build a simple LRU Cache", difficulty: "Hard",
            solutions: [
                { label: "🔵 LinkedHashMap access-order", tip: "Pass true as 3rd constructor arg → get() moves entry to tail.", code: 'int capacity = 3;\n\n// accessOrder=true: get() moves entry to end (most-recently-used)\nLinkedHashMap<Integer, String> lru =\n  new LinkedHashMap<>(capacity, 0.75f, true) {\n    protected boolean removeEldestEntry(\n        Map.Entry<Integer,String> eldest) {\n      return size() > capacity; // evict when full\n    }\n  };\n\nlru.put(1, "A"); lru.put(2, "B"); lru.put(3, "C");\nlru.get(1); // accesses 1 → moves to tail\nlru.put(4, "D"); // cache full → evicts eldest (2)\n\nSystem.out.println(lru); // {3=C, 1=A, 4=D}' },
                { label: "🟠 Manual insertion-order LRU",  tip: "Remove and re-put on every access to move to tail.", code: 'LinkedHashMap<Integer, String> cache = new LinkedHashMap<>();\nint MAX = 3;\n\nvoid put(int key, String val) {\n  if (cache.size() >= MAX && !cache.containsKey(key)) {\n    // Remove the first (oldest) entry\n    int oldest = cache.keySet().iterator().next();\n    cache.remove(oldest);\n  }\n  // Re-inserting moves it to the tail\n  cache.remove(key);\n  cache.put(key, val);\n}' },
                { label: "🟢 Thread-safe LRU (5 lines)",    tip: "Wrap with synchronizedMap for concurrent access.", code: 'final int CAP = 3;\nMap<Integer,String> lru = Collections.synchronizedMap(\n  new LinkedHashMap<Integer,String>(CAP, 0.75f, true) {\n    public boolean removeEldestEntry(Map.Entry<Integer,String> e) {\n      return size() > CAP;\n    }\n  }\n);\n// Thread-safe LRU cache ready!' },
            ],
        },
        {
            title: "Count frequency preserving insertion order", difficulty: "Easy",
            solutions: [
                { label: "🔵 LinkedHashMap + getOrDefault()", tip: "Order of first appearance is preserved.", code: 'String[] words = {"banana","apple","cherry","apple","banana","apple"};\n\nLinkedHashMap<String, Integer> freq = new LinkedHashMap<>();\nfor (String w : words)\n  freq.put(w, freq.getOrDefault(w, 0) + 1);\n\nSystem.out.println(freq);\n// {banana=2, apple=3, cherry=1} ← banana was seen first!' },
                { label: "🟠 LinkedHashMap + merge()",        tip: "merge() avoids the manual null check.", code: 'LinkedHashMap<String, Integer> freq = new LinkedHashMap<>();\nfor (String w : words)\n  freq.merge(w, 1, Integer::sum);\n\nfreq.forEach((k,v) -> System.out.println(k + ": " + v));' },
                { label: "🟢 Stream + toMap (LinkedHashMap)", tip: "Collect into LinkedHashMap with stream.", code: 'LinkedHashMap<String,Long> freq = Arrays.stream(words)\n  .collect(Collectors.groupingBy(\n    w -> w,\n    LinkedHashMap::new,\n    Collectors.counting()));\nSystem.out.println(freq);' },
            ],
        },
        {
            title: "Sort map entries by value (descending)", difficulty: "Medium",
            solutions: [
                { label: "🔵 Stream + sorted + LinkedHashMap", tip: "Sort entries then collect preserving new order.", code: 'LinkedHashMap<String, Integer> scores = new LinkedHashMap<>();\nscores.put("Charlie", 85);\nscores.put("Alice", 92);\nscores.put("Bob", 78);\n\nLinkedHashMap<String, Integer> sorted =\n  scores.entrySet().stream()\n    .sorted(Map.Entry.comparingByValue(Comparator.reverseOrder()))\n    .collect(Collectors.toMap(\n      Map.Entry::getKey, Map.Entry::getValue,\n      (e1,e2) -> e1, LinkedHashMap::new));\n\nSystem.out.println(sorted);\n// {Alice=92, Charlie=85, Bob=78}' },
                { label: "🟠 List + sort + rebuild",          tip: "Convert to list, sort it, rebuild the map.", code: 'List<Map.Entry<String,Integer>> entries =\n  new ArrayList<>(scores.entrySet());\nentries.sort(Map.Entry.comparingByValue(Comparator.reverseOrder()));\n\nLinkedHashMap<String,Integer> sorted = new LinkedHashMap<>();\nfor (var e : entries) sorted.put(e.getKey(), e.getValue());\nSystem.out.println(sorted);' },
                { label: "🟢 TreeMap with value comparator",  tip: "Custom key comparator that compares by value.", code: 'Comparator<String> byVal = (a, b) -> scores.get(b) - scores.get(a);\nTreeMap<String, Integer> sorted = new TreeMap<>(byVal);\nsorted.putAll(scores);\nSystem.out.println(sorted);\n// {Alice=92, Charlie=85, Bob=78}' },
            ],
        },
    ],
};

// ─── Algorithms data ─────────────────────────────────────────
// Algorithms are stored exactly the same shape as collections
// so the same PracticePanel component can render both.

const ALGORITHMS_DATA = {
    icon: "⚡", color: "#60a5fa",
    tagline: "Core algorithm patterns — the building blocks of interviews",
    badge: "java.util.*, java.util.stream.*",
    whenToUse: "Choose the pattern that matches the problem structure",
    declare: "// No single declaration — pattern depends on problem",
    complexity: { get: "varies", add: "varies", remove: "varies", search: "varies" },
    note: "Master the PATTERN first, then the syntax will follow naturally",
    // Algorithms have no methods — only questions
    methods: [],
    questions: [
        {
            title: "Binary Search — find target in sorted array", difficulty: "Easy",
            solutions: [
                { label: "🔵 Collections.binarySearch()", tip: "Built-in — use on sorted List. Returns -(insertion point)-1 when not found.", code: 'ArrayList<Integer> nums = new ArrayList<>(\n  Arrays.asList(2, 5, 8, 12, 16, 23, 38, 56));\n// List MUST be sorted before calling!\n\nint index = Collections.binarySearch(nums, 23);\nSystem.out.println("Found at index: " + index); // 5\n\n// Not found → returns -(insertion point) - 1\nint missing = Collections.binarySearch(nums, 10);\nSystem.out.println(missing); // -4' },
                { label: "🟠 Arrays.binarySearch()",       tip: "For primitive arrays. Supports range search.", code: 'int[] arr = {2, 5, 8, 12, 16, 23, 38, 56};\n\nint idx = Arrays.binarySearch(arr, 23);\nSystem.out.println("Index: " + idx); // 5\n\n// Search only arr[2..5]\nint rangeIdx = Arrays.binarySearch(arr, 2, 6, 16);\nSystem.out.println("In range: " + rangeIdx); // 4' },
                { label: "🟢 Manual implementation",        tip: "Use left+(right-left)/2 to avoid integer overflow!", code: 'static int binarySearch(int[] arr, int target) {\n  int left = 0, right = arr.length - 1;\n  \n  while (left <= right) {\n    // ✅ Avoids overflow vs (left+right)/2\n    int mid = left + (right - left) / 2;\n    \n    if (arr[mid] == target)      return mid;\n    else if (arr[mid] < target)  left  = mid + 1;\n    else                         right = mid - 1;\n  }\n  return -1; // not found\n}' },
            ],
        },
        {
            title: "Sorting — built-in and custom comparators", difficulty: "Easy",
            solutions: [
                { label: "🔵 Collections.sort() / Arrays.sort()", tip: "Built-in TimSort — O(n log n), stable, always prefer this.", code: '// Sort an ArrayList ascending\nArrayList<Integer> nums = new ArrayList<>(\n  Arrays.asList(5, 2, 8, 1, 9, 3));\nCollections.sort(nums);\nSystem.out.println(nums); // [1, 2, 3, 5, 8, 9]\n\n// Descending\nCollections.sort(nums, Collections.reverseOrder());\n\n// Sort a primitive array\nint[] arr = {5, 2, 8, 1, 9};\nArrays.sort(arr);\nSystem.out.println(Arrays.toString(arr));\n\n// Sort partial range arr[1..3]\nArrays.sort(arr, 1, 4);' },
                { label: "🟠 Custom Comparator sort",         tip: "Sort by any property — chain with thenComparing().", code: 'ArrayList<String> names = new ArrayList<>(\n  Arrays.asList("Charlie","Alice","Bob","Dave"));\n\n// Sort by LENGTH, then alphabetically for ties\nnames.sort((a, b) -> {\n  if (a.length() != b.length())\n    return a.length() - b.length();\n  return a.compareTo(b);\n});\nSystem.out.println(names);\n// [Bob, Dave, Alice, Charlie]\n\n// Same thing with Comparator.comparing chain\nnames.sort(Comparator.comparingInt(String::length)\n           .thenComparing(Comparator.naturalOrder()));' },
                { label: "🟢 Merge Sort (manual)",             tip: "Classic divide-and-conquer. O(n log n). Good to know for interviews.", code: 'static void mergeSort(int[] arr, int l, int r) {\n  if (l >= r) return;\n  int mid = (l + r) / 2;\n  mergeSort(arr, l, mid);\n  mergeSort(arr, mid+1, r);\n  merge(arr, l, mid, r);\n}\nstatic void merge(int[] arr, int l, int m, int r) {\n  int[] tmp = Arrays.copyOfRange(arr, l, r+1);\n  int i=0, j=m-l+1, k=l;\n  while (i<=m-l && j<=r-l)\n    arr[k++] = tmp[i]<=tmp[j] ? tmp[i++] : tmp[j++];\n  while (i<=m-l) arr[k++] = tmp[i++];\n  while (j<=r-l) arr[k++] = tmp[j++];\n}' },
            ],
        },
        {
            title: "Two Pointers — two sum in sorted array", difficulty: "Easy",
            solutions: [
                { label: "🔵 Left + right converge",       tip: "If sum too small → move left right. If too large → move right left.", code: 'static int[] twoSum(int[] nums, int target) {\n  int left = 0, right = nums.length - 1;\n  \n  while (left < right) {\n    int sum = nums[left] + nums[right];\n    if (sum == target)       return new int[]{left, right};\n    else if (sum < target)  left++;   // need bigger sum\n    else                    right--;  // need smaller sum\n  }\n  return new int[]{-1, -1}; // not found\n}\n// arr={1,3,5,7,9,11}, target=12 → [1,5] (3+9)' },
                { label: "🟠 Palindrome check",             tip: "Compare chars from both ends converging to centre.", code: 'static boolean isPalindrome(String s) {\n  // Normalise: lowercase, remove non-alphanumeric\n  s = s.toLowerCase().replaceAll("[^a-z0-9]", "");\n  \n  int l = 0, r = s.length() - 1;\n  while (l < r) {\n    if (s.charAt(l) != s.charAt(r)) return false;\n    l++; r--;\n  }\n  return true;\n}\n// "A man a plan a canal Panama" → true' },
                { label: "🟢 Remove duplicates in-place",   tip: "Slow pointer writes; fast pointer reads unique values.", code: 'static int removeDuplicates(int[] nums) {\n  if (nums.length == 0) return 0;\n  int slow = 0; // slow = index of last unique written\n  \n  for (int fast = 1; fast < nums.length; fast++) {\n    if (nums[fast] != nums[slow]) {\n      slow++;\n      nums[slow] = nums[fast]; // write next unique\n    }\n  }\n  return slow + 1; // length of unique part\n}\n// {1,1,2,3,3,4} → 4 unique values' },
            ],
        },
        {
            title: "Sliding Window — max sum subarray of size k", difficulty: "Medium",
            solutions: [
                { label: "🔵 Fixed window slide",            tip: "Add new right element, remove old left element each step.", code: 'static int maxSumSubarray(int[] arr, int k) {\n  // Build first window\n  int windowSum = 0;\n  for (int i = 0; i < k; i++) windowSum += arr[i];\n  \n  int maxSum = windowSum;\n  \n  // Slide: add arr[i], remove arr[i-k]\n  for (int i = k; i < arr.length; i++) {\n    windowSum += arr[i] - arr[i-k];\n    maxSum = Math.max(maxSum, windowSum);\n  }\n  return maxSum;\n}\n// arr={2,1,5,1,3,2}, k=3 → 9 (5+1+3)' },
                { label: "🟠 Longest substring no repeat",   tip: "Variable window — shrink left until duplicate is removed.", code: 'static int lengthOfLongestSubstring(String s) {\n  HashSet<Character> set = new HashSet<>();\n  int left = 0, maxLen = 0;\n  \n  for (int right = 0; right < s.length(); right++) {\n    // Shrink window from left until duplicate gone\n    while (set.contains(s.charAt(right)))\n      set.remove(s.charAt(left++));\n    \n    set.add(s.charAt(right));\n    maxLen = Math.max(maxLen, right - left + 1);\n  }\n  return maxLen;\n}\n// "abcabcbb" → 3 ("abc")' },
                { label: "🟢 Minimum window substring",      tip: "Expand right to find valid window, shrink left to minimise it.", code: 'static String minWindow(String s, String t) {\n  HashMap<Character,Integer> need = new HashMap<>();\n  for (char c : t.toCharArray()) need.merge(c, 1, Integer::sum);\n  \n  int left=0, formed=0, required=need.size();\n  int[] ans = {-1, 0, 0};\n  HashMap<Character,Integer> win = new HashMap<>();\n  \n  for (int right=0; right<s.length(); right++) {\n    char c = s.charAt(right);\n    win.merge(c, 1, Integer::sum);\n    if (need.containsKey(c) && win.get(c).equals(need.get(c))) formed++;\n    while (formed == required) {\n      if (ans[0]==-1 || right-left+1 < ans[0])\n        ans = new int[]{right-left+1, left, right};\n      char l = s.charAt(left++);\n      win.merge(l, -1, Integer::sum);\n      if (need.containsKey(l) && win.get(l)<need.get(l)) formed--;\n    }\n  }\n  return ans[0]==-1 ? "" : s.substring(ans[1],ans[2]+1);\n}' },
            ],
        },
        {
            title: "HashMap Pattern — Two Sum (unsorted)", difficulty: "Easy",
            solutions: [
                { label: "🔵 Complement lookup",             tip: "Store complement → index. O(n) vs O(n²) brute force.", code: 'static int[] twoSum(int[] nums, int target) {\n  // map: value → its index\n  HashMap<Integer, Integer> map = new HashMap<>();\n  \n  for (int i = 0; i < nums.length; i++) {\n    int complement = target - nums[i];\n    // Have we already seen the number we need?\n    if (map.containsKey(complement))\n      return new int[]{map.get(complement), i};\n    map.put(nums[i], i);\n  }\n  return new int[]{};\n}\n// {2,7,11,15}, target=9 → [0,1]' },
                { label: "🟠 Anagram check",                  tip: "Count chars in s, subtract from t — any negative = not anagram.", code: 'static boolean isAnagram(String s, String t) {\n  if (s.length() != t.length()) return false;\n  \n  HashMap<Character,Integer> count = new HashMap<>();\n  for (char c : s.toCharArray())\n    count.merge(c, 1, Integer::sum);\n  for (char c : t.toCharArray()) {\n    count.merge(c, -1, Integer::sum);\n    if (count.get(c) < 0) return false; // more in t than s\n  }\n  return true;\n}\n// isAnagram("anagram","nagaram") → true' },
                { label: "🟢 Subarray sum equals k",          tip: "Prefix sum + HashMap → O(n). Key insight: if (sum-k) seen before, found a valid subarray.", code: 'static int subarraySum(int[] nums, int k) {\n  HashMap<Integer,Integer> prefixCount = new HashMap<>();\n  prefixCount.put(0, 1); // empty prefix\n  int sum = 0, count = 0;\n  \n  for (int n : nums) {\n    sum += n;\n    // (sum - k) times we can form a valid subarray ending here\n    count += prefixCount.getOrDefault(sum-k, 0);\n    prefixCount.merge(sum, 1, Integer::sum);\n  }\n  return count;\n}\n// {1,1,1}, k=2 → 2' },
            ],
        },
        {
            title: "Recursion & Backtracking — generate all subsets", difficulty: "Medium",
            solutions: [
                { label: "🔵 Include/exclude backtracking",  tip: "At each element: CHOOSE to include it, explore, then UN-CHOOSE.", code: 'static List<List<Integer>> subsets(int[] nums) {\n  List<List<Integer>> result = new ArrayList<>();\n  backtrack(nums, 0, new ArrayList<>(), result);\n  return result;\n}\nstatic void backtrack(int[] nums, int start,\n    List<Integer> current, List<List<Integer>> result) {\n  // Snapshot the current combination and save it\n  result.add(new ArrayList<>(current));\n  \n  for (int i = start; i < nums.length; i++) {\n    current.add(nums[i]);       // ← CHOOSE\n    backtrack(nums, i+1, current, result); // ← EXPLORE\n    current.remove(current.size()-1);      // ← UN-CHOOSE\n  }\n}\n// {1,2,3} → [[],[1],[1,2],[1,2,3],[1,3],[2],[2,3],[3]]' },
                { label: "🟠 Permutations via swapping",      tip: "Swap current position with every remaining position, then recurse.", code: 'static List<List<Integer>> permute(int[] nums) {\n  List<List<Integer>> result = new ArrayList<>();\n  permuteHelper(nums, 0, result);\n  return result;\n}\nstatic void permuteHelper(int[] nums, int start,\n    List<List<Integer>> result) {\n  if (start == nums.length) {\n    List<Integer> perm = new ArrayList<>();\n    for (int n : nums) perm.add(n);\n    result.add(perm);\n    return;\n  }\n  for (int i = start; i < nums.length; i++) {\n    swap(nums, start, i);              // fix position start\n    permuteHelper(nums, start+1, result);\n    swap(nums, start, i);              // backtrack\n  }\n}\n// {1,2,3} → 6 permutations' },
                { label: "🟢 Fibonacci with memoization",     tip: "Cache results to turn O(2ⁿ) into O(n). This is called top-down DP.", code: 'static HashMap<Integer,Long> memo = new HashMap<>();\n\nstatic long fib(int n) {\n  if (n <= 1) return n;\n  // Return cached result if we computed this before\n  if (memo.containsKey(n)) return memo.get(n);\n  \n  long result = fib(n-1) + fib(n-2);\n  memo.put(n, result); // cache before returning!\n  return result;\n}\n// fib(50) → 12586269025 (instant with memo)\n// Without memo: 2^50 calls — way too slow!' },
            ],
        },
    ],
};

/**
 * ALL_TOPICS — master list that drives the sidebar navigation.
 * Order here = order in the sidebar.
 * Adding a new topic = add one entry here.
 */
const ALL_TOPICS = [
    { id: "arraylist",      label: "ArrayList",      data: ARRAY_LIST_DATA,      icon: <ListAltIcon /> },
    { id: "hashset",        label: "HashSet",         data: HASH_SET_DATA,         icon: <FilterListIcon /> },
    { id: "hashmap",        label: "HashMap",         data: HASH_MAP_DATA,         icon: <GridOnIcon /> },
    { id: "linkedlist",     label: "LinkedList",      data: LINKED_LIST_DATA,      icon: <LinkIcon /> },
    { id: "treemap",        label: "TreeMap",         data: TREE_MAP_DATA,         icon: <AccountTreeIcon /> },
    { id: "treeset",        label: "TreeSet",         data: TREE_SET_DATA,         icon: <AccountTreeIcon /> },
    { id: "linkedhashset",  label: "LinkedHashSet",   data: LINKED_HASH_SET_DATA,  icon: <StorageIcon /> },
    { id: "linkedhashmap",  label: "LinkedHashMap",   data: LINKED_HASH_MAP_DATA,  icon: <StorageIcon /> },
    { id: "algorithms",     label: "Algorithms",      data: ALGORITHMS_DATA,       icon: <FunctionsIcon /> },
];

// Width of the desktop sidebar in pixels
const DRAWER_WIDTH = 220;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 3 — SMALL REUSABLE COMPONENTS
//  Each component has ONE responsibility (Clean Code rule #2).
//  They receive only the props they need (minimal coupling).
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * ComplexityBadge
 * Renders a single complexity pill (e.g. "get O(1)").
 * Colour-codes green for O(1), amber for O(log n), red for O(n).
 *
 * @param {string} operation - The operation name (e.g. "get")
 * @param {string} value     - The complexity string (e.g. "O(1)")
 */
const ComplexityBadge = ({ operation, value }) => {
    // Determine colour based on complexity string content
    const getComplexityColor = (v) => {
        if (v.includes("1)"))    return APP_COLORS.success;  // O(1) → green
        if (v.includes("log"))   return APP_COLORS.warning;  // O(log n) → amber
        if (v.includes("varies")) return APP_COLORS.textMuted;
        return APP_COLORS.danger;                            // O(n) → red
    };

    return (
        <Box sx={{
            textAlign: "center",
            border: `1px solid ${APP_COLORS.border}`,
            borderRadius: 1.5,
            px: 1.5, py: 0.5,
            minWidth: 64,
            bgcolor: "rgba(0,0,0,0.3)",
        }}>
            {/* Operation label */}
            <Typography variant="caption" sx={{ color: APP_COLORS.textMuted, display: "block", fontSize: 9, textTransform: "uppercase" }}>
                {operation}
            </Typography>
            {/* Complexity value — colour-coded */}
            <Typography variant="caption" sx={{ color: getComplexityColor(value), fontWeight: 700, fontSize: 11 }}>
                {value}
            </Typography>
        </Box>
    );
};

/**
 * CategoryChip
 * Small coloured badge for method categories (Insert, Access, etc.).
 *
 * @param {string} category - One of the keys in CATEGORY_STYLE_MAP
 */
const CategoryChip = ({ category }) => {
    // Fall back to a neutral style if category is not in the map
    const style = CATEGORY_STYLE_MAP[category] || { bg: "#1e2d3d", color: "#8b949e" };

    return (
        <Chip
            label={category}
            size="small"
            sx={{
                bgcolor: style.bg,
                color: style.color,
                fontWeight: 700,
                fontSize: 9,
                height: 20,
                "& .MuiChip-label": { px: 1 },
            }}
        />
    );
};

/**
 * CopyButton
 * Icon button that copies text to clipboard.
 * Shows a check icon for 1.5 s after a successful copy.
 *
 * @param {string} textToCopy - The text that will be copied
 * @param {string} copyId     - Unique id to track which button is "copied"
 * @param {string} copiedId   - Currently copied id (from parent state)
 * @param {fn}     onCopy     - Callback({ id, text }) called after copy
 */
const CopyButton = ({ textToCopy, copyId, copiedId, onCopy }) => {
    const isCopied = copiedId === copyId;

    const handleClick = () => {
        navigator.clipboard.writeText(textToCopy);
        onCopy(copyId);
    };

    return (
        <Tooltip title={isCopied ? "Copied!" : "Copy code"}>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{
                    color: isCopied ? APP_COLORS.success : APP_COLORS.textMuted,
                    "&:hover": { color: APP_COLORS.title },
                    transition: "color 0.2s",
                }}
            >
                {/* Switch icon based on copy state */}
                {isCopied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
            </IconButton>
        </Tooltip>
    );
};

/**
 * CodeBlock
 * A styled <pre> block for displaying Java code snippets.
 * Includes a CopyButton in the top-right corner.
 *
 * @param {string} code     - The Java code to display
 * @param {string} copyId   - Unique id for the copy button
 * @param {string} copiedId - Currently copied id
 * @param {fn}     onCopy   - Copy callback
 */
const CodeBlock = ({ code, copyId, copiedId, onCopy }) => (
    <Box sx={{ position: "relative", mt: 1 }}>
        {/* Copy button floated to top-right */}
        <Box sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}>
            <CopyButton textToCopy={code} copyId={copyId} copiedId={copiedId} onCopy={onCopy} />
        </Box>

        {/* Code display area */}
        <Box
            component="pre"
            sx={{
                bgcolor: APP_COLORS.codeBg,
                border: `1px solid ${APP_COLORS.border}`,
                borderRadius: 1.5,
                p: "12px 36px 12px 14px", // right padding makes room for copy button
                fontSize: 11,
                lineHeight: 1.85,
                color: APP_COLORS.text,
                overflowX: "auto",
                m: 0,
                whiteSpace: "pre",
                fontFamily: "'JetBrains Mono','Fira Code','Courier New',monospace",
            }}
        >
            {code}
        </Box>
    </Box>
);

/**
 * MethodCard
 * Renders ONE method entry: name, category chip, description, code block.
 *
 * @param {object} method  - { name, cat, desc, code }
 * @param {string} color   - Accent colour inherited from collection theme
 * @param {string} index   - Unique string key for copy tracking
 * @param {string} copiedId
 * @param {fn}     onCopy
 */
const MethodCard = ({ method, color, cardIndex, copiedId, onCopy }) => (
    <Card
        sx={{
            bgcolor: APP_COLORS.surface,
            border: `1px solid ${APP_COLORS.border}`,
            borderRadius: 2,
            transition: "border-color 0.2s, transform 0.15s",
            "&:hover": {
                borderColor: color,
                transform: "translateY(-1px)",
            },
        }}
    >
        <CardContent sx={{ p: "12px !important" }}>
            {/* Header row: method name + category chip */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                <Typography
                    variant="caption"
                    sx={{ color, fontWeight: 700, fontSize: 12, fontFamily: "monospace" }}
                >
                    {method.name}
                </Typography>
                <CategoryChip category={method.cat} />
            </Box>

            {/* Description */}
            <Typography variant="caption" sx={{ color: APP_COLORS.textMuted, display: "block", mb: 0.75, lineHeight: 1.5, fontSize: 10.5 }}>
                {method.desc}
            </Typography>

            {/* Code snippet */}
            <CodeBlock
                code={method.code}
                copyId={`method-${cardIndex}`}
                copiedId={copiedId}
                onCopy={onCopy}
            />
        </CardContent>
    </Card>
);

/**
 * SolutionPanel
 * Renders a single practice problem with:
 *   • Three solution tabs (🔵 🟠 🟢)
 *   • Strategy tip
 *   • Code block
 *   • "When to use each" comparison accordion
 *
 * @param {object} question  - { title, difficulty, solutions[] }
 * @param {string} color     - Accent colour from collection
 * @param {string} copiedId
 * @param {fn}     onCopy
 */
const SolutionPanel = ({ question, color, copiedId, onCopy }) => {
    // Track which solution tab is selected (0, 1, or 2)
    const [activeSolution, setActiveSolution] = useState(0);

    const difficultyColor = DIFFICULTY_COLOR_MAP[question.difficulty] || APP_COLORS.textMuted;
    const currentSolution = question.solutions[activeSolution];

    return (
        <Card sx={{
            bgcolor: APP_COLORS.surface,
            border: `1px solid ${color}44`,
            borderRadius: 2,
            overflow: "hidden",
        }}>
            <CardContent sx={{ p: "0 !important" }}>

                {/* ── Problem title bar ── */}
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    p: "12px 16px",
                    bgcolor: "rgba(0,0,0,0.3)",
                    borderBottom: `1px solid ${APP_COLORS.border}`,
                }}>
                    <CodeIcon sx={{ color, fontSize: 18 }} />
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: APP_COLORS.text }}>
                            {question.title}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: difficultyColor }}>
                            ● {question.difficulty} · 3 solutions
                        </Typography>
                    </Box>
                </Box>

                {/* ── Solution selector tabs ── */}
                <Tabs
                    value={activeSolution}
                    onChange={(_, newIdx) => setActiveSolution(newIdx)}
                    sx={{
                        bgcolor: "rgba(0,0,0,0.2)",
                        borderBottom: `1px solid ${APP_COLORS.border}`,
                        minHeight: 38,
                        "& .MuiTab-root": { minHeight: 38, fontSize: 10, fontWeight: 600, color: APP_COLORS.textMuted },
                        "& .Mui-selected": { color: `${color} !important` },
                        "& .MuiTabs-indicator": { bgcolor: color },
                    }}
                >
                    {question.solutions.map((sol, idx) => (
                        <Tab key={idx} label={sol.label} />
                    ))}
                </Tabs>

                {/* ── Strategy tip ── */}
                <Box sx={{ p: "10px 16px", bgcolor: `${color}0d`, borderBottom: `1px solid ${APP_COLORS.border}` }}>
                    <Typography sx={{ fontSize: 11, color: APP_COLORS.text }}>
                        <span style={{ color, fontWeight: 700 }}>💬 Strategy: </span>
                        {currentSolution.tip}
                    </Typography>
                </Box>

                {/* ── Code block ── */}
                <Box sx={{ p: 2 }}>
                    <CodeBlock
                        code={currentSolution.code}
                        copyId={`sol-${question.title}-${activeSolution}`}
                        copiedId={copiedId}
                        onCopy={onCopy}
                    />
                </Box>

                {/* ── "When to use each" comparison accordion ── */}
                <Accordion
                    disableGutters
                    sx={{
                        bgcolor: "transparent",
                        boxShadow: "none",
                        borderTop: `1px solid ${APP_COLORS.border}`,
                        "&:before": { display: "none" }, // remove MUI default divider
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: APP_COLORS.textMuted, fontSize: 16 }} />}
                        sx={{ px: 2, py: 0.5, minHeight: 36 }}
                    >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: APP_COLORS.textMuted, letterSpacing: "0.06em" }}>
                            ⚖️ WHEN TO USE EACH APPROACH
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                        {question.solutions.map((sol, idx) => (
                            <Box key={idx} sx={{ display: "flex", gap: 1.5, mb: idx < 2 ? 1.5 : 0 }}>
                                <Typography sx={{ fontSize: 14, minWidth: 22 }}>
                                    {["🔵", "🟠", "🟢"][idx]}
                                </Typography>
                                <Box>
                                    {/* Solution label without the emoji prefix */}
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: APP_COLORS.text }}>
                                        {sol.label.replace(/^[^ ]+ /, "")}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, mt: 0.25 }}>
                                        {sol.tip}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </AccordionDetails>
                </Accordion>

            </CardContent>
        </Card>
    );
};

/**
 * InfoBar
 * Displays the collection header: name, tagline, declaration,
 * complexity badges, and "when to use" note.
 *
 * @param {object} topicData - Full collection data object
 */
const InfoBar = ({ topicData }) => (
    <Box sx={{ mb: 3 }}>
        {/* Title row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 22 }}>{topicData.icon}</Typography>
            <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 900, color: APP_COLORS.title }}>
                    {topicData.badge}
                </Typography>
                <Typography sx={{ fontSize: 11, color: APP_COLORS.textMuted }}>
                    {topicData.tagline}
                </Typography>
            </Box>
        </Box>

        {/* Declaration code line */}
        <Box sx={{
            display: "inline-block", bgcolor: "rgba(0,0,0,0.4)",
            border: `1px solid ${APP_COLORS.border}`, borderRadius: 1,
            px: 1.5, py: 0.5, mb: 1.5,
        }}>
            <Typography component="code" sx={{ fontSize: 11, color: topicData.color, fontFamily: "monospace" }}>
                {topicData.declare}
            </Typography>
        </Box>

        {/* Complexity badges row + "when to use" */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
            {/* O(n) badges for each operation */}
            {Object.entries(topicData.complexity).map(([op, val]) => (
                <ComplexityBadge key={op} operation={op} value={val} />
            ))}

            {/* "When to use" card */}
            <Box sx={{
                flex: 1, minWidth: 180,
                bgcolor: `${topicData.color}10`,
                border: `1px solid ${topicData.color}33`,
                borderRadius: 1.5, px: 1.5, py: 0.75,
            }}>
                <Typography sx={{ fontSize: 10, color: topicData.color, fontWeight: 700 }}>
                    💡 Use when:
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: APP_COLORS.text }}>
                    {topicData.whenToUse}
                </Typography>
            </Box>
        </Box>

        {/* Footnote */}
        {topicData.note && (
            <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, mt: 0.75, fontStyle: "italic" }}>
                * {topicData.note}
            </Typography>
        )}
    </Box>
);

/**
 * MethodsTab
 * Renders the methods grid with a category filter row on top.
 *
 * @param {object} topicData - Full collection data object
 * @param {string} copiedId
 * @param {fn}     onCopy
 */
const MethodsTab = ({ topicData, copiedId, onCopy }) => {
    // Track which category filter is active; "All" = no filter
    const [activeCategory, setActiveCategory] = useState("All");

    // Derive unique category list once from methods array
    const categories = useMemo(
        () => ["All", ...new Set(topicData.methods.map((m) => m.cat))],
        [topicData.methods]
    );

    // Apply filter — useMemo avoids re-filtering on every render
    const visibleMethods = useMemo(
        () =>
            activeCategory === "All"
                ? topicData.methods
                : topicData.methods.filter((m) => m.cat === activeCategory),
        [topicData.methods, activeCategory]
    );

    // Show placeholder if this topic (e.g. Algorithms) has no methods
    if (topicData.methods.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: APP_COLORS.textMuted }}>
                <SchoolIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                <Typography sx={{ fontSize: 13 }}>
                    No individual methods — see the Practice tab for algorithm patterns.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Category filter chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
                {categories.map((cat) => (
                    <Chip
                        key={cat}
                        label={cat}
                        onClick={() => setActiveCategory(cat)}
                        variant={activeCategory === cat ? "filled" : "outlined"}
                        size="small"
                        sx={{
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: 600,
                            color: activeCategory === cat ? APP_COLORS.background : APP_COLORS.textMuted,
                            bgcolor: activeCategory === cat ? topicData.color : "transparent",
                            borderColor: activeCategory === cat ? topicData.color : APP_COLORS.border,
                            "&:hover": { borderColor: topicData.color, color: topicData.color },
                        }}
                    />
                ))}
            </Box>

            {/* Responsive method cards grid */}
            <Box sx={{
                display: "grid",
                // Auto-fill columns: min 260px wide, max 1fr — responsive without media queries
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 1.5,
            }}>
                {visibleMethods.map((method, idx) => (
                    <MethodCard
                        key={method.name}
                        method={method}
                        color={topicData.color}
                        cardIndex={`${topicData.badge}-${idx}`}
                        copiedId={copiedId}
                        onCopy={onCopy}
                    />
                ))}
            </Box>
        </Box>
    );
};

/**
 * PracticeTab
 * Renders the practice problems for the selected topic.
 * Uses a horizontal question-selector pill row on top.
 *
 * @param {object} topicData - Full collection data object
 * @param {string} copiedId
 * @param {fn}     onCopy
 */
const PracticeTab = ({ topicData, copiedId, onCopy }) => {
    // Track which question is selected
    const [activeQuestion, setActiveQuestion] = useState(0);

    const currentQuestion = topicData.questions[activeQuestion];

    return (
        <Box>
            {/* Question selector pills */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {topicData.questions.map((q, idx) => {
                    const diffColor = DIFFICULTY_COLOR_MAP[q.difficulty] || APP_COLORS.textMuted;
                    const isActive  = activeQuestion === idx;

                    return (
                        <Button
                            key={idx}
                            onClick={() => setActiveQuestion(idx)}
                            variant={isActive ? "contained" : "outlined"}
                            size="small"
                            sx={{
                                textTransform: "none",
                                fontSize: 11,
                                fontWeight: 700,
                                fontFamily: "monospace",
                                borderColor: isActive ? topicData.color : APP_COLORS.border,
                                bgcolor: isActive ? `${topicData.color}22` : "transparent",
                                color: isActive ? topicData.color : APP_COLORS.textMuted,
                                "&:hover": { borderColor: topicData.color, bgcolor: `${topicData.color}11` },
                                display: "flex", flexDirection: "column", alignItems: "flex-start",
                                px: 1.5, py: 0.75, lineHeight: 1.4,
                            }}
                        >
                            <span>#{idx + 1} {q.title}</span>
                            <span style={{ fontSize: 9, color: diffColor }}>● {q.difficulty}</span>
                        </Button>
                    );
                })}
            </Box>

            {/* Active question's solution panel */}
            {currentQuestion && (
                <SolutionPanel
                    question={currentQuestion}
                    color={topicData.color}
                    copiedId={copiedId}
                    onCopy={onCopy}
                />
            )}
        </Box>
    );
};

/**
 * TopicContent
 * The main content area for a selected topic.
 * Switches between Methods tab and Practice tab.
 *
 * @param {object} topicData - Full collection data object
 * @param {string} copiedId
 * @param {fn}     onCopy
 */
const TopicContent = ({ topicData, copiedId, onCopy }) => {
    // "methods" tab is index 0, "practice" tab is index 1
    const [activeTab, setActiveTab] = useState(0);

    // Reset to methods tab whenever the user switches topics
    // (key prop on parent handles this — see App component)

    return (
        <Box>
            {/* Collection info header */}
            <InfoBar topicData={topicData} />

            {/* Methods / Practice tab switcher */}
            <Tabs
                value={activeTab}
                onChange={(_, newVal) => setActiveTab(newVal)}
                sx={{
                    mb: 3,
                    borderBottom: `1px solid ${APP_COLORS.border}`,
                    "& .MuiTab-root": { fontSize: 11, fontWeight: 700, color: APP_COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", minHeight: 40 },
                    "& .Mui-selected": { color: `${topicData.color} !important` },
                    "& .MuiTabs-indicator": { bgcolor: topicData.color },
                }}
            >
                <Tab
                    label="📋 Methods"
                    // Disable methods tab when there are no methods (e.g. Algorithms)
                    disabled={topicData.methods.length === 0}
                />
                <Tab label="🧩 Practice" />
            </Tabs>

            {/* Tab panels — render only the active one */}
            {activeTab === 0 && (
                <MethodsTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />
            )}
            {activeTab === 1 && (
                <PracticeTab topicData={topicData} copiedId={copiedId} onCopy={onCopy} />
            )}
        </Box>
    );
};

/**
 * SidebarContent
 * The list of navigation items rendered inside the Drawer.
 * Accepts an onSelect callback so the parent can close mobile drawer.
 *
 * @param {string}   activeTopicId - Currently selected topic id
 * @param {fn}       onSelect      - Called with topicId when user clicks
 */
const SidebarContent = ({ activeTopicId, onSelect }) => (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

        {/* ── App logo / title ── */}
        <Box sx={{ p: 2, pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <SchoolIcon sx={{ color: APP_COLORS.title, fontSize: 22 }} />
                <Typography sx={{ fontWeight: 900, fontSize: 13, color: APP_COLORS.title, lineHeight: 1.2 }}>
                    Java Complete
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 10, color: APP_COLORS.textMuted, pl: "30px" }}>
                Algo Prep
            </Typography>
        </Box>

        <Divider sx={{ borderColor: APP_COLORS.border }} />

        {/* ── Navigation list ── */}
        <List dense sx={{ flex: 1, py: 1, overflowY: "auto" }}>
            {ALL_TOPICS.map((topic) => {
                const isActive = activeTopicId === topic.id;

                return (
                    <ListItem key={topic.id} disablePadding>
                        <ListItemButton
                            onClick={() => onSelect(topic.id)}
                            sx={{
                                borderRadius: 1.5,
                                mx: 1, my: 0.25,
                                bgcolor: isActive ? `${topic.data.color}22` : "transparent",
                                borderLeft: isActive ? `3px solid ${topic.data.color}` : "3px solid transparent",
                                "&:hover": { bgcolor: `${topic.data.color}11` },
                                transition: "all 0.18s",
                                py: 0.75,
                            }}
                        >
                            {/* Collection icon */}
                            <ListItemIcon sx={{ minWidth: 32, color: isActive ? topic.data.color : APP_COLORS.textMuted }}>
                                {topic.data.icon}
                            </ListItemIcon>

                            {/* Collection label */}
                            <ListItemText
                                primary={topic.label}
                                primaryTypographyProps={{
                                    fontSize: 12,
                                    fontWeight: isActive ? 700 : 400,
                                    color: isActive ? topic.data.color : APP_COLORS.text,
                                }}
                            />

                            {/* Active indicator badge */}
                            {isActive && (
                                <BookmarkIcon sx={{ fontSize: 12, color: topic.data.color }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>

        <Divider sx={{ borderColor: APP_COLORS.border }} />

        {/* ── Footer ── */}
        <Box sx={{ p: 1.5, textAlign: "center" }}>
            <Typography sx={{ fontSize: 9, color: APP_COLORS.textMuted }}>
                ☕ Java Collections &amp; Algorithms
            </Typography>
            <Typography sx={{ fontSize: 9, color: APP_COLORS.textMuted }}>
                8 Collections · 6 Algorithm Patterns
            </Typography>
        </Box>
    </Box>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  SECTION 4 — ROOT APP COMPONENT
//  Manages global state:
//    • Which topic is selected
//    • Whether mobile drawer is open
//    • Which code block was last copied
//  All child components receive these via props.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * AppContent
 * The inner shell (AppBar + Drawer + main area).
 * Separated from JavaCompleteAlgoPrep so the theme
 * is already active when this renders.
 */
const AppContent = () => {
    const theme   = useTheme();
    // isMobile is true when the screen is narrower than "md" (960px)
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // ── State ─────────────────────────────────────────────────
    // Which topic the user is currently viewing
    const [activeTopicId, setActiveTopicId] = useState("arraylist");

    // Whether the mobile drawer is currently open
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // ID of the code block most recently copied (for showing ✓ icon)
    const [copiedId, setCopiedId] = useState(null);

    // ── Derived values ────────────────────────────────────────
    // Find the currently selected topic's data — O(n) but n=9 max
    const activeTopic = ALL_TOPICS.find((t) => t.id === activeTopicId);

    // ── Handlers ─────────────────────────────────────────────

    /**
     * handleTopicSelect
     * Called when the user clicks a sidebar navigation item.
     * On mobile, also closes the drawer for better readability.
     */
    const handleTopicSelect = (topicId) => {
        setActiveTopicId(topicId);
        // Close mobile drawer automatically after selection
        if (isMobile) setMobileDrawerOpen(false);
    };

    /**
     * handleCopy
     * Called by CopyButton with the copy ID.
     * Sets copiedId so buttons can show the ✓ icon.
     * Automatically clears after 1.5 s.
     */
    const handleCopy = (id) => {
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    // ── Sidebar drawer ─────────────────────────────────────────
    // On desktop: permanent drawer (always visible)
    // On mobile:  temporary drawer (slides in/out)
    const drawerVariant = isMobile ? "temporary" : "permanent";

    const drawerContent = (
        <SidebarContent
            activeTopicId={activeTopicId}
            onSelect={handleTopicSelect}
        />
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: APP_COLORS.background }}>

            {/* ── Top AppBar (mobile only) ── */}
            {isMobile && (
                <AppBar
                    position="fixed"
                    sx={{
                        bgcolor: APP_COLORS.drawerBg,
                        borderBottom: `1px solid ${APP_COLORS.border}`,
                        zIndex: theme.zIndex.drawer + 1,
                        boxShadow: "none",
                    }}
                >
                    <Toolbar variant="dense" sx={{ gap: 1, minHeight: 48 }}>
                        {/* Hamburger menu button */}
                        <IconButton
                            edge="start"
                            size="small"
                            onClick={() => setMobileDrawerOpen(true)}
                            sx={{ color: APP_COLORS.title }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Current topic name in AppBar */}
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: APP_COLORS.title, flex: 1 }}>
                            {activeTopic?.label ?? "Java Prep"}
                        </Typography>

                        {/* Collection icon badge */}
                        <Typography sx={{ fontSize: 18 }}>
                            {activeTopic?.data.icon}
                        </Typography>
                    </Toolbar>
                </AppBar>
            )}

            {/* ── Desktop permanent sidebar ── */}
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        "& .MuiDrawer-paper": {
                            width: DRAWER_WIDTH,
                            bgcolor: APP_COLORS.drawerBg,
                            borderRight: `1px solid ${APP_COLORS.border}`,
                            overflowX: "hidden",
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            {/* ── Mobile temporary drawer ── */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileDrawerOpen}
                    onClose={() => setMobileDrawerOpen(false)}
                    ModalProps={{ keepMounted: true }} // Better mobile performance
                    sx={{
                        "& .MuiDrawer-paper": {
                            width: DRAWER_WIDTH,
                            bgcolor: APP_COLORS.drawerBg,
                            borderRight: `1px solid ${APP_COLORS.border}`,
                        },
                    }}
                >
                    {/* Close button inside mobile drawer */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", p: 0.5 }}>
                        <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} sx={{ color: APP_COLORS.textMuted }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {drawerContent}
                </Drawer>
            )}

            {/* ── Main content area ── */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // On mobile, push content below the AppBar
                    mt: isMobile ? "48px" : 0,
                    // On desktop, sidebar is permanent so no offset needed
                    minWidth: 0, // prevent flex children overflow
                    overflowX: "hidden",
                }}
            >
                <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 } }}>

                    {/* ── Desktop header (no AppBar on desktop) ── */}
                    {!isMobile && (
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 2, mb: 3,
                            pb: 2, borderBottom: `1px solid ${APP_COLORS.border}`,
                        }}>
                            <Typography sx={{ fontSize: 24 }}>{activeTopic?.data.icon}</Typography>
                            <Box>
                                <Typography sx={{ fontWeight: 900, fontSize: 18, color: APP_COLORS.title }}>
                                    {activeTopic?.label}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: APP_COLORS.textMuted }}>
                                    Java Complete Algo Prep — Collections &amp; Algorithms Study Guide
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/*
           * key={activeTopicId} forces React to UNMOUNT and REMOUNT
           * TopicContent whenever the topic changes.
           * This resets all internal tab/filter state automatically
           * without needing to manually lift state or call resets.
           */}
                    {activeTopic && (
                        <TopicContent
                            key={activeTopicId}
                            topicData={activeTopic.data}
                            copiedId={copiedId}
                            onCopy={handleCopy}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

/**
 * JavaCompleteAlgoPrep — ROOT EXPORT
 * Wraps everything in the MUI ThemeProvider and CssBaseline.
 * CssBaseline applies a CSS reset and sets the body background.
 */
export default function JavaCompleteAlgoPrep() {
    // Build the theme once — useMemo prevents rebuilding on every render
    const appTheme = useMemo(() => buildAppTheme(), []);

    return (
        <ThemeProvider theme={appTheme}>
            {/* CssBaseline: MUI's CSS reset + applies theme background colour */}
            <CssBaseline />
            <AppContent />
        </ThemeProvider>
    );
}
